import { useState, useEffect } from 'react';
import axios from 'axios';  
import Navbar from '../../components/navBar';
import LikeButton from '../../components/LikeButton';
import { format } from 'date-fns';

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [clubDetails, setClubDetails] = useState(null);
  const [playerTitles, setPlayerTitles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/players`);
        setPlayers(response.data);
        
        // Get current user from localStorage
        const userId = localStorage.getItem('userId');
        if (userId) {
          setCurrentUser({ userId: parseInt(userId) });
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch players');
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  const fetchClubDetails = async (clubId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/clubs/${clubId}`);
      setClubDetails(response.data);
    } catch (err) {
      console.error('Failed to fetch club details:', err);
    }
  };

  const fetchPlayerTitles = async (playerId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/titles`);
      const titles = response.data.filter(title => 
        title.winningPlayers?.some(player => player.playerId === playerId)
      );
      setPlayerTitles(titles);
    } catch (err) {
      console.error('Failed to fetch player titles:', err);
    }
  };

  const handlePlayerClick = async (player) => {
    setSelectedPlayer(player);
    if (player.clubId) {
      await fetchClubDetails(player.clubId);
    } else {
      setClubDetails(null);
    }
    await fetchPlayerTitles(player.playerId);
  };

  if (loading) return <div className="text-white">Loading...</div>;
  if (error) return <div className="text-white">Error: {error}</div>;

  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{ 
          backgroundImage: "url('https://t4.ftcdn.net/jpg/06/19/39/53/360_F_619395337_xfoBmAMfDhh0Wg5NRwVUUsZxJrUq8xBn.jpg')",
          zIndex: 0 
        }}
      />
      
      {/* Content Container */}
      <Navbar />
      <div className="relative z-10 container mx-auto px-4 py-8 min-h-screen">
        <h1 className="text-4xl font-bold text-white mb-8 text-center pt-20">Players</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-w-[1920px] mx-auto">
          {players.map((player) => (
            <div 
              key={player.playerId}
              onClick={() => handlePlayerClick(player)}
              className="bg-gray-900 backdrop-blur-sm rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-[1.02] p-6 cursor-pointer"
            >
              <div className="text-center space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden">
                  <img 
                    src={player.profileImageUrl} 
                    alt={player.name}
                    className="w-full h-full rounded-full object-cover transform hover:scale-110 transition-transform duration-300 bg-gray-700 p-2"
                    onError={(e) => {
                      e.target.src = '/images/default-avatar.jpg'; // Add a default avatar image
                    }}
                  />
                </div>
                <h2 className="text-2xl font-bold text-white">{player.name}</h2>
                <span className="text-white text-sm font-medium bg-gray-700/80 px-4 py-2 rounded-full inline-block">
                  {player.position}
                </span>
                {currentUser && (
                  <div onClick={(e) => e.stopPropagation()} className="flex justify-center">
                    <LikeButton
                      entityId={player.playerId}
                      entityType="Player"
                      userId={currentUser.userId}
                      size="small"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Modal - Updated styling */}
        {selectedPlayer && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-900 backdrop-blur-sm rounded-xl max-w-4xl w-full p-8 relative border border-gray-700 shadow-2xl">
              <button 
                onClick={() => setSelectedPlayer(null)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full p-2 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-1/3">
                  <div className="relative overflow-hidden rounded-xl bg-gray-700 p-4">
                    <img 
                      src={selectedPlayer.profileImageUrl} 
                      alt={selectedPlayer.name}
                      className="w-full h-80 object-cover rounded-lg hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
                
                <div className="w-full md:w-2/3 text-white space-y-6">
                  <div className="flex items-start justify-between">
                    <h2 className="text-3xl font-bold text-white">{selectedPlayer.name}</h2>
                    {currentUser && (
                      <LikeButton
                        entityId={selectedPlayer.playerId}
                        entityType="Player"
                        userId={currentUser.userId}
                        size="large"
                      />
                    )}
                  </div>
                  <div className="space-y-4">
                    <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                      <span className="text-red-500 font-semibold">Position:</span>
                      <span className="ml-2 text-gray-200">{selectedPlayer.position}</span>
                    </div>
                    <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                      <span className="text-red-500 font-semibold">Age:</span>
                      <span className="ml-2 text-gray-200">{selectedPlayer.age}</span>
                    </div>
                    {selectedPlayer.preferredLeg && (
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Preferred Leg:</span>
                        <span className="ml-2 text-gray-200 capitalize">{selectedPlayer.preferredLeg}</span>
                      </div>
                    )}
                    {selectedPlayer.bio && (
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Bio:</span>
                        <p className="mt-2 text-gray-200 leading-relaxed">{selectedPlayer.bio}</p>
                      </div>
                    )}
                    {clubDetails && (
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Club:</span>
                        <div className="mt-2">
                          <p className="text-gray-200 font-medium">{clubDetails.name}</p>
                          {clubDetails.location && (
                            <p className="text-gray-400 text-sm mt-1">{clubDetails.location}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Titles Section */}
                    {playerTitles.length > 0 && (
                      <div className="bg-gray-800/50 p-4 rounded-lg backdrop-blur-sm">
                        <span className="text-red-500 font-semibold">Titles Won:</span>
                        <div className="mt-2 space-y-3">
                          {playerTitles.map((title) => (
                            <div key={title.titleId} className="bg-gray-700/50 p-3 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="text-white font-medium">{title.name}</h4>
                                  <p className="text-gray-300 text-sm">{title.competition}</p>
                                  <p className="text-gray-400 text-sm">Season: {title.season}</p>
                                </div>
                                <span className="text-gray-400 text-sm">
                                  {format(new Date(title.dateWon), 'MMM dd, yyyy')}
                                </span>
                              </div>
                              {title.winningClub && (
                                <p className="text-gray-400 text-sm mt-1">
                                  with {title.winningClub.name}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
